import React, { useEffect, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import { santaUrl } from "../utils/santaApi";

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

const SPRITES = [
  ["aircraft", "/assets/aircraft-icon.png"],
  ["santa-east-1", "/assets/Santa-East-1.png"],
  ["santa-east-2", "/assets/Santa-East-2.png"],
  ["santa-west-1", "/assets/Santa-West-1.png"],
  ["santa-west-2", "/assets/Santa-West-2.png"],
];

function loadSprite(map, id, src) {
  return new Promise((resolve) => {
    map.loadImage(src, (err, img) => {
      if (err || !img) {
        console.error("❌ Failed to load sprite:", src);
        resolve();
        return;
      }
      if (!map.hasImage(id)) map.addImage(id, img);
      resolve();
    });
  });
}

function santaIconFor(direction, frame) {
  if (direction === "east") return frame === 0 ? "santa-east-1" : "santa-east-2";
  return frame === 0 ? "santa-west-1" : "santa-west-2";
}

// Sleigh size depends on zoom. Parked at the pole it is a tiny speck from far
// away (the easter egg) that grows as you zoom in on it. While he is flying in
// December it is larger so people can spot him on the world view.
const SANTA_SIZE_PARKED = [
  "interpolate", ["linear"], ["zoom"],
  0, 0.02, 2, 0.03, 4, 0.08, 6, 0.2, 8, 0.35,
];
const SANTA_SIZE_ACTIVE = [
  "interpolate", ["linear"], ["zoom"],
  0, 0.08, 2, 0.14, 4, 0.25, 6, 0.35,
];

export default function MapView({ selectedFlight }) {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const santaFrameRef = useRef(0);
  const santaBaseFrameRef = useRef(0);

  const [mapReady, setMapReady] = useState(false);
  const [santa, setSanta] = useState(null); // latest /api/santa/state snapshot

  /* ===============================
     INIT MAP (ONCE)
  =============================== */
  useEffect(() => {
    if (mapRef.current) return;

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: "mapbox://styles/mapbox/dark-v11",
      projection: "globe",
      center: [0, 30],
      zoom: 1.8,
    });

    mapRef.current = map;

    map.on("load", async () => {
      console.log("🗺️ Mapbox loaded");

      // Wait for every sprite so nothing tries to draw an icon that isn't there yet
      await Promise.all(SPRITES.map(([id, src]) => loadSprite(map, id, src)));
      setMapReady(true);
    });

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  /* ===============================
     SANTA STATE (parked / countdown / active / done)
     Polls fast while he is flying, slowly the rest of the year.
  =============================== */
  useEffect(() => {
    let cancelled = false;
    let timer = null;

    const load = async () => {
      let nextDelay = 5 * 60_000;

      try {
        const res = await fetch(santaUrl("state"));
        const data = await res.json();
        if (!cancelled) setSanta(data);
        if (data.state === "active") nextDelay = 30_000;
      } catch (err) {
        console.error("Santa state error", err);
      }

      if (!cancelled) timer = setTimeout(load, nextDelay);
    };

    load();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, []);

  /* ===============================
     SANTA ON THE MAP
     Parked at the North Pole all year (hidden surprise), live in December.
     Hidden while the user is actively tracking him, because the tracked
     entity layer below draws him then.
  =============================== */
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;

    const removeLayer = () => {
      if (map.getLayer("santa-base")) map.removeLayer("santa-base");
      if (map.getSource("santa-base")) map.removeSource("santa-base");
    };

    const position = santa?.position;
    if (!position || selectedFlight?.source === "santa") {
      removeLayer();
      return;
    }

    // Animate the two sleigh frames only while he is actually flying
    if (santa.state === "active") {
      santaBaseFrameRef.current = (santaBaseFrameRef.current + 1) % 2;
    } else {
      santaBaseFrameRef.current = 0;
    }

    const icon = santaIconFor(position.direction, santaBaseFrameRef.current);
    const size = santa.state === "active" ? SANTA_SIZE_ACTIVE : SANTA_SIZE_PARKED;
    const data = {
      type: "Feature",
      geometry: { type: "Point", coordinates: [position.lon, position.lat] },
    };

    const source = map.getSource("santa-base");
    if (source) {
      source.setData(data);
      map.setLayoutProperty("santa-base", "icon-image", icon);
      map.setLayoutProperty("santa-base", "icon-size", size);
      return;
    }

    map.addSource("santa-base", { type: "geojson", data });
    map.addLayer({
      id: "santa-base",
      type: "symbol",
      source: "santa-base",
      layout: {
        "icon-image": icon,
        "icon-size": size,
        "icon-allow-overlap": true,
        "icon-ignore-placement": true,
        "icon-anchor": "center",
      },
    });
  }, [santa, selectedFlight, mapReady]);

  /* ===============================
     RENDER ENTITY (AIRCRAFT / TRACKED SANTA)
  =============================== */
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;

    // Remove previous entity cleanly
    if (map.getLayer("entity")) map.removeLayer("entity");
    if (map.getSource("entity")) map.removeSource("entity");

    // Nothing to render
    if (!selectedFlight?.position) return;

    const { lat, lon, direction } = selectedFlight.position;
    if (lat == null || lon == null) return;

    const isSanta = selectedFlight.source === "santa";

    // Santa animation frame (toggle 0 / 1)
    santaFrameRef.current = (santaFrameRef.current + 1) % 2;

    const santaIcon = santaIconFor(direction, santaFrameRef.current);

    map.addSource("entity", {
      type: "geojson",
      data: {
        type: "Feature",
        geometry: {
          type: "Point",
          coordinates: [lon, lat],
        },
      },
    });

    map.addLayer({
      id: "entity",
      type: "symbol",
      source: "entity",
      layout: {
        "icon-image": isSanta ? santaIcon : "aircraft",
        "icon-size": isSanta ? 0.35 : 0.6,
        "icon-allow-overlap": true,
        "icon-ignore-placement": true,
        "icon-anchor": "center",
      },
    });

    // Smooth follow
    map.flyTo({
      center: [lon, lat],
      zoom: isSanta ? 2.6 : 4.8,
      speed: 0.5,
      curve: 1.2,
      essential: true,
    });
  }, [selectedFlight, mapReady]);

  return (
    <div
      ref={mapContainerRef}
      className="fd-map"
      style={{ width: "100%", minHeight: 520 }}
    />
  );
}
